import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import { siteMetadata } from "../../gatsby-config";
import { useLocation } from '@reach/router';
import { escapeJsonLd } from "../utils/json-ld";
import { toAbsoluteUrl } from "../utils/absolute-url";

// shareImage arrive sous plusieurs formes selon le template : chemin déjà
// résolu (produit), fichier Strapi { localFile: { publicURL } } (homepage,
// article, catégories), ou objet { publicURL } / { url }.
const getShareImagePath = (image) => {
  if (!image) return "";
  if (typeof image === "string") return image;
  return (image.localFile && image.localFile.publicURL) || image.publicURL || image.url || "";
};

const SEO = ({ seo = { titleNoTrail: false } }) => {
  const location = useLocation();
  const { strapiGlobal } = useStaticQuery(query);
  const { defaultSeo, siteName } = strapiGlobal;

  // Merge default and page-specific SEO values
  const fullSeo = { ...defaultSeo, ...seo };

  const canonicalUrl = seo.canonical || (location && location.pathname ? siteMetadata.siteUrl + location.pathname : siteMetadata.siteUrl);

  const getMetaTags = () => {
    const tags = [];

    if (fullSeo.metaTitle) {
      tags.push(
        {
          property: "og:title",
          content: fullSeo.metaTitle,
        },
        {
          name: "twitter:title",
          content: fullSeo.metaTitle,
        }
      );
    }
    if (fullSeo.metaDescription) {
      tags.push(
        {
          name: "description",
          content: fullSeo.metaDescription && fullSeo.metaDescription.length > 155 ? (fullSeo.metaDescription || "").slice(0, 152) + '...' : fullSeo.metaDescription,
        },
        {
          property: "og:description",
          content: fullSeo.metaDescription,
        },
        {
          name: "twitter:description",
          content: fullSeo.metaDescription,
        }
      );
    }
    // Une page sans visuel propre (ex. page.image absent) retombe sur l'image par défaut.
    const imageUrl =
      toAbsoluteUrl(getShareImagePath(fullSeo.shareImage)) ||
      toAbsoluteUrl(getShareImagePath(defaultSeo && defaultSeo.shareImage));
    if (imageUrl) {
      tags.push(
        {
          name: "image",
          content: imageUrl,
        },
        {
          property: "og:image",
          content: imageUrl,
        },
        {
          name: "twitter:image",
          content: imageUrl,
        }
      );
    }
    tags.push(
      { property: "og:type", content: fullSeo.article ? "article" : "website" },
      { property: "og:url", content: canonicalUrl },
      { property: "og:site_name", content: siteName },
      { property: "og:locale", content: "fr_FR" }
    );
    tags.push({ name: "twitter:card", content: "summary_large_image" });
    tags.push({ name: "theme-color", content: "#174aa5" });

    return tags;
  };

  const noTrail = fullSeo.metaTitle.split('|').length > 1
    || (`${fullSeo.metaTitle} | ${siteName}`.length > 65)
    || seo.titleNoTrail === true;

  const metaTags = getMetaTags();

  return (
    <Helmet
      title={fullSeo.metaTitle.length > 65 && fullSeo.metaTitle.split('|').length > 1 ? fullSeo.metaTitle.split('|')[0] : fullSeo.metaTitle}
      titleTemplate={noTrail ? `%s` : `%s | ${siteName}`}
      link={[
        {
          rel: "icon",
          type: "image/png",
          sizes: "32x32",
          href: "/favicon-32x32.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "64x64",
          href: "/favicon-64x64.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "128x128",
          href: "/favicon-128x128.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "192x192",
          href: "/favicon-192x192.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "512x512",
          href: "/favicon-512x512.png",
        },
        {
          rel: "apple-touch-icon",
          sizes: "192x192",
          href: "/favicon-192x192.png",
        },
        {
          rel: 'canonical',
          href: canonicalUrl
        }
      ]}
      meta={metaTags}
    >
      {fullSeo && fullSeo.scripts && [
        ...fullSeo.scripts,
        {
          type: "application/ld+json",
          content: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://latelierdutotebag.com/#organization",
            "name": "L'Atelier du Tote Bag",
            "url": "https://latelierdutotebag.com/",
            "logo": "https://latelierdutotebag.com/static/b5085e6df4730d67075b097525220197/e850a/Logo-latelierducoton.png",
            "email": "devis@latelierdutotebag.com",
            "telephone": "+33 9 72 63 01 32",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "10 rue Guiglia",
              "postalCode": "06000",
              "addressLocality": "Nice",
              "addressCountry": "FR"
            },
            "identifier": {
              "@type": "PropertyValue",
              "propertyID": "RCS de Paris",
              "value": "822628251"
            },
            "sameAs": [
              "https://www.facebook.com/latelierdutotebag",
              "https://www.instagram.com/latelierdutotebag"
            ]
          })
        },
        {
          type: "application/ld+json",
          content: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://latelierdutotebag.com/#website",
            "url": "https://latelierdutotebag.com/",
            "name": "L'Atelier du Tote Bag",
            "description": "Fabricant français de tote bags personnalisés pour professionnels. Impression textile de qualité, livraison rapide et prix dégressifs.",
            "publisher": {
              "@type": "Organization",
              "@id": "https://latelierdutotebag.com/#organization"
            },
            "inLanguage": "fr-FR",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://latelierdutotebag.com/?s={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })
        },
      ].map((script, i) => (
        <script key={i} type={script.type}>
          {script.type === "application/ld+json"
            ? escapeJsonLd(script.content)
            : script.content}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  article: PropTypes.bool,
};

SEO.defaultProps = {
  title: null,
  description: null,
  image: null,
  article: false,
};

const query = graphql`
  query {
    strapiGlobal {
      siteName
      defaultSeo {
        metaTitle
        metaDescription
        shareImage {
          localFile {
            publicURL
          }
        }
      }
    }
  }
`;
